// Collectible relics — meta-progression unlocks that drop from bosses.
//
// Unlike the per-run cards in ui.js CARDS (consumed each session), relics
// persist across runs in localStorage and act as toggleable perks on the
// main menu. Each one is opt-in per run so players can still attempt a
// "vanilla" challenge after collecting everything.
//
// Effects are wired wherever they need to read game state — Game.update
// checks `activeRelics.has('reflexes')` to fire auto-attacks, the off-
// screen indicator pass renders coin arrows when 'coinSense' is active,
// and the guard render hook draws range/HP when 'engineerEye' is active.

import { t } from './i18n.js';

export const RELICS = [
  {
    id: 'reflexes',
    icon: '⚡',
    get label() { return t('relic.reflexes.label'); },
    get desc() { return t('relic.reflexes.desc'); },
  },
  {
    id: 'coinSense',
    icon: '💰',
    get label() { return t('relic.coinSense.label'); },
    get desc() { return t('relic.coinSense.desc'); },
  },
  {
    id: 'engineerEye',
    icon: '📐',
    get label() { return t('relic.engineerEye.label'); },
    get desc() { return t('relic.engineerEye.desc'); },
  },
  // Time-warp relics. Stackable in the active set: with both on, the
  // game eases into bullet-time near threats AND fast-forwards through
  // empty stretches. Thresholds + factors live in Game._relicTimeScale.
  // Retired from drops (2026-10-28): little use and the speed changes break
  // the flow. Players who own them keep them (toggle in the menu).
  {
    id: 'timeSlow',
    retired: true,
    icon: '⏳',
    get label() { return t('relic.timeSlow.label'); },
    get desc() { return t('relic.timeSlow.desc'); },
  },
  {
    id: 'timeFast',
    retired: true,
    icon: '⏩',
    get label() { return t('relic.timeFast.label'); },
    get desc() { return t('relic.timeFast.desc'); },
  },
  {
    id: 'miserGrip',
    icon: '🪙',
    get label() { return t('relic.miserGrip.label'); },
    get desc() { return t('relic.miserGrip.desc'); },
  },
  // Visual-only feedback relic: red vignette that intensifies as HP
  // drops below BLOOD_FOG_THRESHOLD. No mechanical effect — just a
  // peripheral-vision tell so the player doesn't have to glance at the
  // HP bar during a melee scramble.
  {
    id: 'bloodFog',
    icon: '🩸',
    get label() { return t('relic.bloodFog.label'); },
    get desc() { return t('relic.bloodFog.desc'); },
  },
];

export const RELIC_IDS = RELICS.map(r => r.id);

// Relics a boss can still drop: `retired` ones stay in RELICS (owners keep
// them) but are never unlocked again.
export const RELIC_DROP_IDS = RELICS.filter(r => !r.retired).map(r => r.id);

export function getRelic(id) {
  return RELICS.find(r => r.id === id) || null;
}

// Drop chance per boss kill, applied only when there is at least one
// undiscovered relic in RELICS. At 20% a player sees their first drop
// after ~3 boss kills on average (1 - 0.8^3 ≈ 49%), and reaching wave 20
// (~4 bosses) makes a drop likely (1 - 0.8^4 ≈ 59%). Bumped from 0.08 —
// prod telemetry showed even players who killed 8 bosses in a run cleared
// only a coin-flip's worth of relics, so the meta layer was effectively
// invisible past the wave-10 pity drop.
export const RELIC_DROP_CHANCE = 0.20;

// Pity wave: a player who has never unlocked any relic is guaranteed
// to receive their first one from the boss on this wave. Avoids the
// situation where a new player grinds 8 boss kills without ever seeing
// the meta-progression layer. Once the player owns at least one relic
// the pity rule disengages and drops fall back to RELIC_DROP_CHANCE.
export const RELIC_PITY_WAVE = 10;
